import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import { hasCostCenterConfigurationPending } from 'modules/cost_centers/lib/costCenterHelpers'
import { CostCenterConfigurations } from 'modules/cost_centers/lib/types'

import { EmptyState } from './EmptyState'
import { getColumns } from './columns'
import { useJobCatalogColumns } from './hooks/useJobCatalogColumns'
import { usePeopleAssignmentDataSource } from './hooks/usePeopleAssignmentDataSource'
import { FormattedCostCenterEmployeeWithStructuredAssignment } from './types'

const CostCentersPeopleAssignment = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const client = useApolloClient()
  const {
    dev_test_de_verdad,
    canManageCostCenters,
    hasFeatureCostCenterManagement,
    configurations,
  } = useOutletContext<{
    dev_test_de_verdad: boolean
    canManageCostCenters: boolean
    hasFeatureCostCenterManagement: boolean
    configurations: CostCenterConfigurations
  }>()

  const hasConfigurationPending = useUnsafeMemo(() => {
    return hasCostCenterConfigurationPending(configurations)
  }, [configurations])

  const shouldShowJobCatalogColumns = useJobCatalogColumns(configurations)

  const handleItemClick = (item: FormattedCostCenterEmployeeWithStructuredAssignment) => {
    navigate(navigation.settings.finance.costCenters.people.edit.fullPath({ id: item.id }))
  }

  const source = usePeopleAssignmentDataSource({
    client,
    onItemClick: handleItemClick,
  })

  const columns = getColumns(shouldShowJobCatalogColumns)

  if (hasConfigurationPending || configurations.length === 0) {
    return <EmptyState hasConfigurationPending={hasConfigurationPending} />
  }

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(_, selectedItems) => {
          const checkedItems = selectedItems.itemsStatus.filter((item) => item.checked)
          const employeeIds = checkedItems.map((item) => item.item.id)
          const legalEntityIds = [checkedItems[0]?.item.legalEntity?.id]

          navigate(navigation.settings.finance.costCenters.people.create.fullPath(), {
            state: {
              employeeIds,
              legalEntityIds,
            },
          })
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns,
            },
          },
        ]}
      />
      <Outlet
        context={{
          dev_test_de_verdad,
          canManageCostCenters,
          hasNewStructureEnabled: true,
          hasFeatureCostCenterManagement,
        }}
      />
    </>
  )
}

export { CostCentersPeopleAssignment }
